import { useEffect, useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Button } from 'shared/ui/button'
import { Input } from 'shared/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select'
import { Badge } from 'shared/ui/badge'
import { Separator } from 'shared/ui/separator'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'shared/ui/dialog'
import { Label } from 'shared/ui/label'
import { Switch } from 'shared/ui/switch'
import { toast } from 'sonner'
import {
  createWorkflow,
  deleteWorkflowVersion,
  getWorkflowTemplateVersions,
  searchWorkflows,
  updateWorkflowStatus,
  type WorkflowTemplateVersionOptionDto,
  type WorkflowListItemDto,
} from 'modules/admin/api/workflowConfigurationService'
import { Loader2, Plus, RefreshCw, Search, Pencil, Copy, Trash2, Power } from 'lucide-react'

interface WorkflowListViewProps {
  tenantId: string
  onOpenDesigner: (workflowVersionId: string) => void
}

type StatusFilter = 'all' | 'active' | 'inactive'
type ResubmitPolicy = 'RESTART' | 'RETURNED'

const getWorkflowLabel = (workflow: WorkflowListItemDto) => {
  if (workflow.workflowName && workflow.workflowName.trim().length > 0) {
    return workflow.workflowName
  }
  if (workflow.workflowCode && workflow.workflowCode.trim().length > 0) {
    return workflow.workflowCode
  }
  return `WF-${workflow.workflowVersionId.slice(0, 8).toUpperCase()}`
}

const getTemplateLabel = (workflow: WorkflowListItemDto) => {
  if (workflow.templateName && workflow.templateName.trim().length > 0) {
    return workflow.templateName
  }
  if (workflow.permitCode && workflow.permitCode.trim().length > 0) {
    return workflow.permitCode
  }
  return 'Template ไม่ระบุ'
}

const RESUBMIT_POLICY_OPTIONS = [
  { value: 'RESTART' as const, label: 'RESTART' },
  { value: 'RETURNED' as const, label: 'RETURNED' },
]

export function WorkflowListView({ tenantId, onOpenDesigner }: WorkflowListViewProps) {
  const [items, setItems] = useState<WorkflowListItemDto[]>([])
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [templateOptions, setTemplateOptions] = useState<WorkflowTemplateVersionOptionDto[]>([])
  const [isTemplateLoading, setIsTemplateLoading] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [createForm, setCreateForm] = useState<{
    templateVersionId: string
    workflowName: string
    workflowCode: string
    priority: number
    isActive: boolean
    copyFromWorkflowVersionId: string
    resubmitPolicy: ResubmitPolicy
  }>({
    templateVersionId: '',
    workflowName: '',
    workflowCode: '',
    priority: 10,
    isActive: false,
    copyFromWorkflowVersionId: '',
    resubmitPolicy: 'RESTART',
  })

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    searchWorkflows({
      tenantId,
      search: searchTerm,
      status: statusFilter,
      page,
      pageSize,
    })
      .then((result) => {
        if (cancelled) return
        setItems(result.items)
        setTotalItems(result.totalItems)
        setTotalPages(result.totalPages || 1)
      })
      .catch(() => {
        if (cancelled) return
        toast.error('ไม่สามารถโหลดรายการ workflow ได้')
        setItems([])
        setTotalItems(0)
        setTotalPages(1)
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [tenantId, searchTerm, statusFilter, page, pageSize, refreshKey])

  useEffect(() => {
    if (!isCreateOpen) {
      return
    }
    let cancelled = false
    setIsTemplateLoading(true)
    getWorkflowTemplateVersions(tenantId)
      .then((result) => {
        if (cancelled) return
        setTemplateOptions(result)
        if (result.length > 0) {
          setCreateForm((prev) => ({
            ...prev,
            templateVersionId: prev.templateVersionId || result[0].templateVersionId,
          }))
        }
      })
      .catch(() => {
        if (cancelled) return
        toast.error('ไม่สามารถโหลด Template Versions ได้')
        setTemplateOptions([])
      })
      .finally(() => {
        if (!cancelled) {
          setIsTemplateLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [isCreateOpen, tenantId])

  const refreshList = () => {
    setRefreshKey((prev) => prev + 1)
  }

  const handleSearch = () => {
    setPage(1)
    setSearchTerm(searchInput.trim())
  }

  const handleClearFilters = () => {
    setSearchInput('')
    setSearchTerm('')
    setStatusFilter('all')
    setPage(1)
  }

  const handleOpenCreateDialog = () => {
    setCreateForm({
      templateVersionId: '',
      workflowName: '',
      workflowCode: '',
      priority: 10,
      isActive: false,
      copyFromWorkflowVersionId: '',
      resubmitPolicy: 'RESTART',
    })
    setCreateError(null)
    setIsCreateOpen(true)
  }

  const handleCreateWorkflow = async () => {
    if (
      !createForm.templateVersionId ||
      createForm.workflowName.trim().length === 0 ||
      createForm.workflowCode.trim().length === 0
    ) {
      setCreateError('กรุณากรอกข้อมูลให้ครบถ้วน')
      return
    }
    setCreateError(null)
    setIsCreating(true)
    try {
      const result = await createWorkflow({
        tenantId,
        templateVersionId: createForm.templateVersionId,
        workflowName: createForm.workflowName.trim(),
        workflowCode: createForm.workflowCode.trim().toUpperCase(),
        priority: createForm.priority,
        isActive: createForm.isActive,
        resubmitPolicy: createForm.resubmitPolicy,
        copyFromWorkflowVersionId:
          createForm.copyFromWorkflowVersionId && createForm.copyFromWorkflowVersionId.length > 0
            ? createForm.copyFromWorkflowVersionId
            : undefined,
      })
      toast.success('สร้าง Workflow สำเร็จ')
      setIsCreateOpen(false)
      refreshList()
      onOpenDesigner(result.metadata.workflowVersionId)
    } catch (error) {
      toast.error('สร้าง Workflow ไม่สำเร็จ')
    } finally {
      setIsCreating(false)
    }
  }

  const handleDelete = async (item: WorkflowListItemDto) => {
    if (!item.canDelete) {
      toast.error('Workflow นี้ถูกใช้งานแล้ว ไม่สามารถลบได้')
      return
    }
    const confirmed = window.confirm(`ยืนยันการลบ Workflow ${item.workflowName}?`)
    if (!confirmed) {
      return
    }
    try {
      await deleteWorkflowVersion(item.workflowVersionId)
      toast.success('ลบ Workflow สำเร็จ')
      refreshList()
    } catch (error) {
      toast.error('ลบ Workflow ไม่สำเร็จ')
    }
  }

  const handleToggleStatus = async (item: WorkflowListItemDto) => {
    try {
      await updateWorkflowStatus(item.workflowVersionId, !item.isActive)
      toast.success(item.isActive ? 'ปิดการใช้งาน Workflow แล้ว' : 'เปิดใช้งาน Workflow แล้ว')
      refreshList()
    } catch (error) {
      toast.error('เปลี่ยนสถานะ Workflow ไม่สำเร็จ')
    }
  }

  const handleCopy = (item: WorkflowListItemDto) => {
    setCreateForm({
      templateVersionId: item.templateVersionId,
      workflowName: `${item.workflowName} (Copy)`,
      workflowCode: `${item.workflowCode}-COPY`,
      priority: item.priority,
      isActive: false,
      copyFromWorkflowVersionId: item.workflowVersionId,
      resubmitPolicy: item.resubmitPolicy ?? 'RESTART',
    })
    setCreateError(null)
    setIsCreateOpen(true)
  }

  const paginationSummary = useMemo(() => {
    if (totalItems === 0) {
      return 'ไม่พบข้อมูล'
    }
    const start = (page - 1) * pageSize + 1
    const end = Math.min(page * pageSize, totalItems)
    return `แสดง ${start}-${end} จากทั้งหมด ${totalItems} รายการ`
  }, [page, pageSize, totalItems])

  return (
    <Card className="border border-gray-200">
      <CardHeader className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="text-lg font-semibold text-gray-900">Workflow Catalog</CardTitle>
            <p className="text-sm text-gray-500">จัดการ workflow ทั้งหมดและเข้าหน้า designer ได้จากที่นี่</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={refreshList}>
              <RefreshCw className="mr-2 h-3.5 w-3.5" />
              Refresh
            </Button>
            <Button onClick={handleOpenCreateDialog}>
              <Plus className="mr-2 h-4 w-4" />
              New Workflow
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[2fr_1fr_auto]">
          <div className="flex items-center rounded-md border border-gray-200 px-3 py-2">
            <Search className="mr-2 h-4 w-4 text-gray-400" />
            <Input
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="ค้นหา Workflow, Template, Site"
              className="h-7 border-none p-0 text-sm focus-visible:ring-0"
            />
          </div>
          <Select
            value={statusFilter}
            onValueChange={(value: StatusFilter) => {
              setStatusFilter(value)
              setPage(1)
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="เลือกสถานะ" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={handleSearch}>
              ค้นหา
            </Button>
            <Button variant="ghost" onClick={handleClearFilters}>
              ล้างค่า
            </Button>
          </div>
        </div>
      </CardHeader>
      <Separator />
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-500">
          <span>{paginationSummary}</span>
          <div className="flex items-center gap-2">
            <span>Page size</span>
            <Select
              value={pageSize.toString()}
              onValueChange={(value) => {
                setPageSize(Number(value))
                setPage(1)
              }}
            >
              <SelectTrigger className="h-8 w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 20, 50].map((size) => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Workflow</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Template / Site</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Permit</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Version</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Priority</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Usage</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Status</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {isLoading ? (
                <tr>
                  <td className="px-4 py-8 text-center text-gray-500" colSpan={8}>
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      กำลังโหลดข้อมูล...
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td className="px-4 py-8 text-center text-gray-500" colSpan={8}>
                    ไม่พบ workflow ที่ตรงกับเงื่อนไข
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.workflowVersionId}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-900">{getWorkflowLabel(item)}</p>
                      <p className="text-xs text-gray-500">
                        {item.workflowCode && item.workflowCode.trim().length > 0
                          ? item.workflowCode
                          : item.workflowVersionId.slice(0, 8).toUpperCase()}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-900">{getTemplateLabel(item)}</p>
                      <p className="text-xs text-gray-500">{item.siteName?.trim().length ? item.siteName : 'ไม่ระบุไซต์'}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{item.permitCode?.trim().length ? item.permitCode : '-'}</td>
                    <td className="px-4 py-3 text-gray-700">v{item.version}</td>
                    <td className="px-4 py-3 text-gray-700">{item.priority ?? '-'}</td>
                    <td className="px-4 py-3 text-gray-700">{item.usageCount ?? 0}</td>
                    <td className="px-4 py-3">
                      <Badge variant={item.isActive ? 'default' : 'secondary'}>
                        {item.isActive ? 'ACTIVE' : 'INACTIVE'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => onOpenDesigner(item.workflowVersionId)}>
                          <Pencil className="mr-1 h-4 w-4" />
                          Edit
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleCopy(item)}>
                          <Copy className="mr-1 h-4 w-4" />
                          Copy
                        </Button>
                        {item.canDelete ? (
                          <Button size="sm" variant="destructive" onClick={() => handleDelete(item)}>
                            <Trash2 className="mr-1 h-4 w-4" />
                            Delete
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => handleToggleStatus(item)}>
                            <Power className="mr-1 h-4 w-4" />
                            {item.isActive ? 'Deactivate' : 'Activate'}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-500">{paginationSummary}</p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            >
              ก่อนหน้า
            </Button>
            <span className="text-sm text-gray-600">
              หน้า {page} / {totalPages || 1}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((prev) => prev + 1)}
            >
              ถัดไป
            </Button>
          </div>
        </div>
        <Dialog
          open={isCreateOpen}
          onOpenChange={(open) => {
            setIsCreateOpen(open)
            if (!open) {
              setCreateError(null)
            }
          }}
        >
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>สร้าง Workflow ใหม่</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-gray-600">Template Version</Label>
                <Select
                  disabled={isTemplateLoading}
                  value={createForm.templateVersionId || 'SELECT_TEMPLATE_VERSION'}
                  onValueChange={(value) => {
                    if (value === 'SELECT_TEMPLATE_VERSION') {
                      setCreateForm((prev) => ({ ...prev, templateVersionId: '' }))
                      return
                    }
                    setCreateForm((prev) => ({ ...prev, templateVersionId: value }))
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={isTemplateLoading ? 'กำลังโหลด...' : 'เลือก Template Version'} />
                  </SelectTrigger>
                  <SelectContent>
                    {[
                      ...templateOptions.map((option) => ({
                        label: `${option.templateName} (v${option.version}) · ${option.status}`,
                        value: option.templateVersionId,
                      })),
                      {
                        label: 'เลือก Template Version อื่น',
                        value: 'SELECT_TEMPLATE_VERSION',
                      },
                    ].map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Copy จาก Workflow เดิม (ถ้ามี)</Label>
                <Select
                  value={createForm.copyFromWorkflowVersionId || 'NO_COPY'}
                  onValueChange={(value) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      copyFromWorkflowVersionId: value === 'NO_COPY' ? '' : value,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="ไม่คัดลอกโครง" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NO_COPY">— ไม่คัดลอก —</SelectItem>
                    {items.map((workflow) => (
                      <SelectItem key={workflow.workflowVersionId} value={workflow.workflowVersionId}>
                        {getWorkflowLabel(workflow)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-gray-500">เลือก workflow ที่ต้องการคัดลอก stage/rules เป็นจุดเริ่มต้น</p>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-sm text-gray-600">Workflow Name</Label>
                  <Input
                    value={createForm.workflowName}
                    onChange={(event) => setCreateForm((prev) => ({ ...prev, workflowName: event.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Workflow Code</Label>
                  <Input
                    value={createForm.workflowCode}
                    onChange={(event) =>
                      setCreateForm((prev) => ({ ...prev, workflowCode: event.target.value.toUpperCase() }))
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <Label className="text-sm text-gray-600">Priority</Label>
                  <Input
                    type="number"
                    min={1}
                    value={createForm.priority}
                    onChange={(event) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        priority: Number(event.target.value) || 1,
                      }))
                    }
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Resubmit Policy</Label>
                  <Select
                    value={createForm.resubmitPolicy}
                    onValueChange={(value) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        resubmitPolicy: value as ResubmitPolicy,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select resubmit policy" />
                    </SelectTrigger>
                    <SelectContent>
                      {RESUBMIT_POLICY_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-gray-500">
                    {createForm.resubmitPolicy === 'RETURNED'
                      ? 'Resume from the returned stage only.'
                      : 'Restart workflow from the first stage.'}
                  </p>
                </div>
                <div className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Active</p>
                    <p className="text-xs text-gray-500">เปิดใช้งานทันทีเมื่อสร้าง</p>
                  </div>
                  <Switch
                    checked={createForm.isActive}
                    onCheckedChange={(checked) => setCreateForm((prev) => ({ ...prev, isActive: checked }))}
                  />
                </div>
              </div>
              {createError ? <p className="text-sm text-red-500">{createError}</p> : null}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)} disabled={isCreating}>
                ยกเลิก
              </Button>
              <Button onClick={handleCreateWorkflow} disabled={isCreating || isTemplateLoading}>
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    กำลังบันทึก...
                  </>
                ) : (
                  'สร้าง Workflow'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  )
}

export default WorkflowListView
